import { DialogContent, Grid, Stack, styled } from '@mui/material';
import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import { WsDialog, WsButton, ControlledTextInput, Label, WsColors } from 'wisestep-base';

import {
  SaveIconButton,
  EditIconButton,
  DeleteIconButton,
} from '../../../features/crmSettings/components/StyledIconButtons';
import { BoldLabel } from '../../../features/jobBoardSettings/components/accountSettings/SharedUserList';
import { IJobCategory } from '../../../features/jobs/helper/JobHelperService';
import { warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';

interface IJobCategoryEditPopup {
  open: boolean;
  closeDialog(): void;
  jobCategoryData: IJobCategory;
  onDataUpdate?: (updatedData) => void;
}

export const StyledListItem = styled(Label)({
  backgroundColor: WsColors.White,
  borderRadius: 4,
  border: '2.5px solid' + WsColors.TheBlues400,
  cursor: 'pointer',
  padding: 4,
  alignContent: 'center',
});

export const GridWithBorder = styled(Grid)({
  border: 'solid',
  borderColor: WsColors.TheBlues400,
  borderRadius: 4,
  borderWidth: 2.5,
  alignItems: 'center',
  paddingLeft: 4,
  justifyContent: 'center',
});
const JobCategoryEditPopup = ({
  open,
  closeDialog,
  jobCategoryData,
  onDataUpdate,
}: IJobCategoryEditPopup) => {
  const methods = useForm();
  const setNotification = useNotificationStore((state) => state.setNotification);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [editedOccupations, setEditedOccupations] = useState(jobCategoryData.occupations);

  const handleEditClick = (index: number) => {
    setEditingIndex(index);
  };

  const handleSaveClick = (index: number) => {
    setEditingIndex(index);
  };

  const handleDeleteClick = (index: number) => {
    const newOccupations = [...editedOccupations];
    newOccupations.splice(index, 1);
    setEditedOccupations(newOccupations);
  };

  const onAddNewOccupation = () => {
    const newOccupation = {
      value: uuidv4(),
      label: methods.getValues('editOccupationName') ?? '',
      occupations: [],
    };
    if (editedOccupations.length > 0) {
      const updatedOccupations = [...editedOccupations, newOccupation];
      setEditedOccupations(updatedOccupations);
      setEditingIndex(updatedOccupations.length - 1);
    } else {
      setEditedOccupations([newOccupation]);
      setEditingIndex(0);
    }
  };

  const onSaveHandler = async () => {
    if (jobCategoryData) {
      try {
        const dataToSend = {
          ...jobCategoryData,
          occupations: editedOccupations,
        };
        onDataUpdate && onDataUpdate(dataToSend);
        closeDialog();
      } catch (error) {
        setNotification(warning('Something Went Wrong, Please try again'));
      }
    }
  };

  return (
    <WsDialog openDialog={open} size="large" onClose={() => close}>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSaveHandler)}>
          <DialogContent>
            <Grid container item xs={12} pb={1}>
              <Grid item xs={5}>
                <BoldLabel> Add or Edit Occupation for Category</BoldLabel>
              </Grid>
              <Grid item xs={7} display="flex" justifyContent="end">
                <Stack direction="row" spacing={1.5}>
                  <WsButton type="submit">Submit</WsButton>
                  <WsButton onClick={() => closeDialog()}>Close</WsButton>
                  <WsButton onClick={onAddNewOccupation}>Add New Occupation</WsButton>
                </Stack>
              </Grid>
            </Grid>
            <Grid alignContent="center" justifyContent="center" display="flex" container mb={1}>
              <Grid justifyContent="center" display="flex" alignItems="center" item xs={6}>
                <BoldLabel>Job Category</BoldLabel>
              </Grid>
              <Grid justifyContent="center" alignItems="center" display="flex" item xs={6}>
                <BoldLabel>Occupation</BoldLabel>
              </Grid>
            </Grid>
            <Stack direction="row" spacing={2} justifyContent="space-between">
              <Grid direction="column" container>
                <StyledListItem>
                  <BoldLabel>{jobCategoryData.label}</BoldLabel>
                </StyledListItem>
              </Grid>
              <Grid container component="form" direction="row" gap={1}>
                {editedOccupations.map((occupation, index) => (
                  <GridWithBorder marginLeft={2} justifyContent="center" item container key={index}>
                    {editingIndex === index ? (
                      <Grid item xs={12} container>
                        <ControlledTextInput
                          name="editOccupationName"
                          value={occupation.label}
                          onChange={(e) => {
                            const newOccupations = [...editedOccupations];
                            newOccupations[index].label = e.target.value;
                            setEditedOccupations(newOccupations);
                          }}
                        />
                      </Grid>
                    ) : (
                      <Grid item xs={8}>
                        <BoldLabel>{occupation.label}</BoldLabel>
                      </Grid>
                    )}
                    <Grid item xs={2} display="flex" justifyContent="end">
                      {editingIndex === index ? (
                        <>
                          <EditIconButton onClick={() => handleEditClick(index)} />
                          <SaveIconButton onClick={() => handleSaveClick(-1)} />
                        </>
                      ) : (
                        <EditIconButton onClick={() => handleEditClick(index)} />
                      )}
                      <DeleteIconButton onClick={() => handleDeleteClick(index)} />
                    </Grid>
                  </GridWithBorder>
                ))}
              </Grid>
            </Stack>
          </DialogContent>
        </form>
      </FormProvider>
    </WsDialog>
  );
};

export default JobCategoryEditPopup;
